import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { usePageTitle } from "@/hooks/usePageTitle";
import { withBranding } from "@/utils/helper";
import { cn } from "@/utils/cn";
import { ConversationList } from "./_components/ConversationList";
import { ChatRoom } from "./_components/ChatRoom";
import { ChatLanding } from "./_components/ChatLanding";
import { NewChatDialog, type NewChatStart } from "./_components/NewChatDialogs";
import { DYNAMIC_CONVERSATION_ID_KEY, MS_CHAT_PATHS } from "@/routes/my-space/routes.chat";

/**
 * Employee chat: the open conversation with the list beside it, on the
 * inline-end side. Below `lg` only one pane shows — the list, or the room
 * when a conversation is open.
 */
export const ChatPage = () => {
  const { t } = useTranslation("chat");
  usePageTitle(withBranding(t("page.title")));
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const params = useParams<{ [DYNAMIC_CONVERSATION_ID_KEY]: string }>();
  const conversationId = Number(params[DYNAMIC_CONVERSATION_ID_KEY]) || null;
  /** "Create new" — null: closed. */
  const [newStart, setNewStart] = useState<NewChatStart | null>(null);

  // Keep the list's own params (tab, search, archived) while switching rooms.
  const withListParams = (path: string) => {
    const qs = searchParams.toString();
    return qs ? `${path}?${qs}` : path;
  };
  const open = (id: number) => navigate(withListParams(MS_CHAT_PATHS.CONVERSATION(id)));
  // A message search result: the chat opens on that message (ChatRoom reads ?message=).
  const openMessage = (id: number, messageId: number) => {
    const qs = new URLSearchParams(searchParams);
    qs.set("message", String(messageId));
    navigate(`${MS_CHAT_PATHS.CONVERSATION(id)}?${qs.toString()}`);
  };
  // Chat Info's drawer state lives in the URL (?info=1), see ChatRoom.
  const openInfo = (id: number) => {
    const qs = new URLSearchParams(searchParams);
    qs.set("info", "1");
    navigate(`${MS_CHAT_PATHS.CONVERSATION(id)}?${qs.toString()}`);
  };

  return (
    <div className="flex h-full min-h-0 flex-1 overflow-hidden bg-background">
      <aside
        className={cn(
          // The list sits on the inline-end side (right in English, left in
          // Arabic); it stays first in the DOM so keyboard focus reaches it first.
          "h-full w-full shrink-0 border-border lg:order-last lg:block lg:w-[340px] lg:border-s xl:w-[380px]",
          conversationId ? "hidden" : "block",
        )}
      >
        <ConversationList
          selectedId={conversationId}
          onSelect={open}
          onOpenInfo={openInfo}
          onNew={(start = "menu") => setNewStart(start)}
          onOpenMessage={openMessage}
          onDeleted={(id) => {
            if (id === conversationId) navigate(withListParams(MS_CHAT_PATHS.ROOT));
          }}
        />
      </aside>

      <section
        className={cn(
          "h-full min-w-0 flex-1 lg:block",
          conversationId ? "block" : "hidden",
        )}
      >
        {conversationId ? (
          <ChatRoom
            key={conversationId}
            conversationId={conversationId}
            onBack={() => navigate(withListParams(MS_CHAT_PATHS.ROOT))}
          />
        ) : (
          <ChatLanding onOpen={open} onNew={setNewStart} />
        )}
      </section>

      <NewChatDialog
        open={newStart != null}
        start={newStart ?? "menu"}
        onClose={() => setNewStart(null)}
        onOpened={open}
      />
    </div>
  );
};
